import { Link } from "react-router-dom";

/* ─── Types ─────────────────────────────────────────────────── */
export type FinancialData = {
  totalContractValue: number;     // sum of all contracts total_amount
  addOnsTotal: number;            // sum of contract_charges amount
  paymentsReceived: number;       // sum of confirmed/paid/completed payments
  openBalance: number;            // sum of contracts balance_amount (active)
  pendingPaymentsAmount: number;  // sum of payments with status=pending
  thisMonthRevenue: number;       // payments confirmed this calendar month
  lastMonthRevenue: number;       // payments confirmed last calendar month
  collectionRate: number;         // paymentsReceived / totalContractValue * 100
};

interface Props {
  financial: FinancialData;
}

/* ─── Helpers ───────────────────────────────────────────────── */
function fmt(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  return `$${n.toLocaleString()}`;
}

function fmtFull(n: number): string {
  return `$${Math.round(n).toLocaleString()}`;
}

/* ─── Mini progress bar ─────────────────────────────────────── */
function ProgressBar({ value, color }: { value: number; color: string }) {
  const clamped = Math.min(100, Math.max(0, value));
  return (
    <div className="h-1 w-full bg-slate-100 rounded-full mt-2 overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${color}`}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

/* ─── Month-over-month delta ────────────────────────────────── */
function MonthDelta({ current, previous }: { current: number; previous: number }) {
  if (previous === 0 && current === 0) return null;
  if (previous === 0) {
    return (
      <span className="text-[10px] font-semibold text-emerald-600 flex items-center gap-0.5">
        <i className="ri-arrow-up-line text-[10px]"></i>
        New
      </span>
    );
  }
  const delta = ((current - previous) / previous) * 100;
  const isUp = delta >= 0;
  return (
    <span
      className={`text-[10px] font-semibold flex items-center gap-0.5 ${
        isUp ? "text-emerald-600" : "text-red-500"
      }`}
    >
      <i className={`${isUp ? "ri-arrow-up-line" : "ri-arrow-down-line"} text-[10px]`}></i>
      {Math.abs(delta).toFixed(0)}% vs last month
    </span>
  );
}

/* ─── Main component ────────────────────────────────────────── */
export default function DashboardFinancial({ financial }: Props) {
  const {
    totalContractValue,
    addOnsTotal,
    paymentsReceived,
    openBalance,
    pendingPaymentsAmount,
    thisMonthRevenue,
    lastMonthRevenue,
    collectionRate,
  } = financial;

  return (
    <div className="space-y-3">
      {/* Section header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center text-[#C8A96E]">
            <i className="ri-line-chart-line text-sm"></i>
          </span>
          <h2 className="text-[#0f172a] font-bold text-sm">Financial Overview</h2>
        </div>
        <Link
          to="/admin/payments"
          className="text-[#C8A96E] hover:text-[#a6853e] text-xs transition-colors cursor-pointer whitespace-nowrap"
        >
          View payments →
        </Link>
      </div>

      {/* Top row — 4 main cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">

        {/* Total Contract Value */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-slate-400 text-[10px] font-semibold uppercase tracking-wider">Contract Value</span>
            <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-100">
              <i className="ri-file-text-line text-slate-500 text-xs"></i>
            </span>
          </div>
          <p className="text-xl font-bold text-[#0f172a]" title={fmtFull(totalContractValue)}>
            {fmt(totalContractValue)}
          </p>
          <p className="text-slate-400 text-xs mt-1">total contracted</p>
          <ProgressBar value={collectionRate} color="bg-[#C8A96E]" />
          <p className="text-[10px] text-slate-400 mt-1">{collectionRate.toFixed(0)}% collected</p>
        </div>

        {/* Payments Received */}
        <Link
          to="/admin/payments"
          className="bg-white rounded-xl border border-slate-200 p-4 hover:border-emerald-200 transition-colors cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-slate-400 text-[10px] font-semibold uppercase tracking-wider">Revenue</span>
            <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-50">
              <i className="ri-money-dollar-circle-line text-emerald-600 text-xs"></i>
            </span>
          </div>
          <p className="text-xl font-bold text-emerald-600 group-hover:text-emerald-700 transition-colors" title={fmtFull(paymentsReceived)}>
            {fmt(paymentsReceived)}
          </p>
          <p className="text-slate-400 text-xs mt-1">payments confirmed</p>
          {pendingPaymentsAmount > 0 && (
            <p className="text-amber-600 text-[10px] mt-1.5 flex items-center gap-1">
              <i className="ri-time-line text-[10px]"></i>
              {fmt(pendingPaymentsAmount)} pending
            </p>
          )}
        </Link>

        {/* Open Balance */}
        <div className={`bg-white rounded-xl border p-4 ${openBalance > 0 ? "border-amber-200" : "border-slate-200"}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-slate-400 text-[10px] font-semibold uppercase tracking-wider">Open Balance</span>
            <span className={`w-7 h-7 flex items-center justify-center rounded-lg ${openBalance > 0 ? "bg-amber-50" : "bg-slate-100"}`}>
              <i className={`ri-scales-3-line text-xs ${openBalance > 0 ? "text-amber-600" : "text-slate-400"}`}></i>
            </span>
          </div>
          <p className={`text-xl font-bold ${openBalance > 0 ? "text-amber-600" : "text-[#0f172a]"}`} title={fmtFull(openBalance)}>
            {fmt(openBalance)}
          </p>
          <p className="text-slate-400 text-xs mt-1">
            {openBalance > 0 ? "from active contracts" : "all balances cleared"}
          </p>
        </div>

        {/* Add-ons */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-slate-400 text-[10px] font-semibold uppercase tracking-wider">Add-ons</span>
            <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#C8A96E]/10">
              <i className="ri-add-box-line text-[#C8A96E] text-xs"></i>
            </span>
          </div>
          <p className="text-xl font-bold text-[#a6853e]" title={fmtFull(addOnsTotal)}>
            {fmt(addOnsTotal)}
          </p>
          <p className="text-slate-400 text-xs mt-1">contract charges</p>
        </div>
      </div>

      {/* Bottom row — monthly revenue */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

        {/* This month */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-slate-400 text-[10px] font-semibold uppercase tracking-wider mb-2">This Month</p>
              <p className="text-2xl font-bold text-[#0f172a]" title={fmtFull(thisMonthRevenue)}>
                {fmt(thisMonthRevenue)}
              </p>
              <div className="mt-1.5">
                <MonthDelta current={thisMonthRevenue} previous={lastMonthRevenue} />
              </div>
            </div>
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-50 flex-shrink-0">
              <i className="ri-calendar-check-line text-emerald-600 text-sm"></i>
            </div>
          </div>
          {/* Simple bar comparing this vs last */}
          {(thisMonthRevenue > 0 || lastMonthRevenue > 0) && (() => {
            const max = Math.max(thisMonthRevenue, lastMonthRevenue, 1);
            return (
              <div className="mt-3 flex items-end gap-2 h-8">
                {lastMonthRevenue > 0 && (
                  <div className="flex flex-col items-center gap-1 flex-1">
                    <div
                      className="w-full bg-slate-200 rounded-t-sm"
                      style={{ height: `${Math.round((lastMonthRevenue / max) * 28)}px`, minHeight: "4px" }}
                    />
                    <span className="text-[9px] text-slate-400 whitespace-nowrap">last</span>
                  </div>
                )}
                <div className="flex flex-col items-center gap-1 flex-1">
                  <div
                    className="w-full bg-emerald-400 rounded-t-sm"
                    style={{ height: `${Math.round((thisMonthRevenue / max) * 28)}px`, minHeight: "4px" }}
                  />
                  <span className="text-[9px] text-emerald-600 font-semibold whitespace-nowrap">now</span>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Last month */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-slate-400 text-[10px] font-semibold uppercase tracking-wider mb-2">Last Month</p>
              <p className="text-2xl font-bold text-slate-600" title={fmtFull(lastMonthRevenue)}>
                {fmt(lastMonthRevenue)}
              </p>
              <p className="text-slate-400 text-xs mt-1.5">
                {lastMonthRevenue === 0
                  ? "No payments last month"
                  : `${fmtFull(lastMonthRevenue)} confirmed`}
              </p>
            </div>
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-100 flex-shrink-0">
              <i className="ri-calendar-line text-slate-400 text-sm"></i>
            </div>
          </div>

          {/* Breakdown pill */}
          {(totalContractValue > 0) && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-400">Contracts</span>
                <span className="font-semibold text-slate-600">{fmt(totalContractValue)}</span>
              </div>
              <div className="flex items-center justify-between text-xs mt-1">
                <span className="text-slate-400">Add-ons</span>
                <span className="font-semibold text-[#a6853e]">{fmt(addOnsTotal)}</span>
              </div>
              <div className="flex items-center justify-between text-xs mt-1 pt-1 border-t border-slate-100">
                <span className="text-slate-500 font-semibold">Total pipeline</span>
                <span className="font-bold text-[#0f172a]">{fmt(totalContractValue + addOnsTotal)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}